// ./src/hooks/useEmailTemplates.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  EmailTemplate,
  EmailTemplateService,
  EmailTemplateFilter,
  EmailTemplateInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseEmailTemplatesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<EmailTemplateFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** User ID for change detection */
  userId?: string | null;
}

export interface UseEmailTemplatesReturn {
  // Data state
  emailTemplate: EmailTemplate | null;
  emailTemplates: EmailTemplate[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Individual item loading state
  loadingEmailTemplateIds: string[];
  isLoadingEmailTemplate: (id: string) => boolean;

  // Search and filters
  filters: Partial<EmailTemplateFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isPreviewing: boolean;
  isPublishing: boolean;
  removingEmailTemplateId: string | null;
  selectedEmailTemplate: EmailTemplate | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<EmailTemplate | null>;
  list: (
    filter?: Partial<EmailTemplateFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<EmailTemplate[]>;
  create: (params: EmailTemplateInput) => Promise<EmailTemplate | null>;
  update: (
    id: string,
    params: EmailTemplateInput
  ) => Promise<EmailTemplate | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;
  preview: (id: string, sampleData?: string) => Promise<EmailTemplate | null>;
  publish: (id: string) => Promise<EmailTemplate | null>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<EmailTemplateFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedEmailTemplate: (emailTemplate: EmailTemplate | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useEmailTemplates = (
  options: UseEmailTemplatesOptions = {}
): UseEmailTemplatesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate | null>(
    null
  );
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Individual item loading state - tracks IDs currently being loaded
  const [loadingEmailTemplateIds, setLoadingEmailTemplateIds] = useState<
    string[]
  >([]);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<EmailTemplateFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [removingEmailTemplateId, setRemovingEmailTemplateId] = useState<
    string | null
  >(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] =
    useState<EmailTemplate | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => emailTemplates.length === 0 && !loading,
    [emailTemplates.length, loading]
  );

  // Helper to check if a specific email template is loading
  const isLoadingEmailTemplate = useCallback(
    (id: string) => loadingEmailTemplateIds.includes(id),
    [loadingEmailTemplateIds]
  );

  // Helper to add ID to loading set
  const addLoadingId = useCallback((id: string) => {
    setLoadingEmailTemplateIds((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  }, []);

  // Helper to remove ID from loading set
  const removeLoadingId = useCallback((id: string) => {
    setLoadingEmailTemplateIds((prev) =>
      prev.filter((loadingId) => loadingId !== id)
    );
  }, []);

  // Helper to add multiple IDs to loading set
  const addLoadingIds = useCallback((ids: string[]) => {
    setLoadingEmailTemplateIds((prev) => {
      const newIds = ids.filter((id) => !prev.includes(id));
      return newIds.length > 0 ? [...prev, ...newIds] : prev;
    });
  }, []);

  // Helper to remove multiple IDs from loading set
  const removeLoadingIds = useCallback((ids: string[]) => {
    setLoadingEmailTemplateIds((prev) =>
      prev.filter((loadingId) => !ids.includes(loadingId))
    );
  }, []);

  // Helper function to update email template in list if it exists, or add it if not
  const updateOrAddEmailTemplateInList = useCallback(
    (updatedEmailTemplate: EmailTemplate) => {
      setEmailTemplates((prev) => {
        const existingIndex = prev.findIndex(
          (t) => t.id === updatedEmailTemplate.id
        );
        if (existingIndex >= 0) {
          const newEmailTemplates = [...prev];
          newEmailTemplates[existingIndex] = updatedEmailTemplate;
          return newEmailTemplates;
        } else {
          return [updatedEmailTemplate, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove email template from list if it exists
  const removeEmailTemplateFromList = useCallback((emailTemplateId: string) => {
    setEmailTemplates((prev) => prev.filter((t) => t.id !== emailTemplateId));
  }, []);

  // Helper function to remove multiple email templates from list
  const removeEmailTemplatesFromList = useCallback(
    (emailTemplateIds: string[]) => {
      const idsSet = new Set(emailTemplateIds);
      setEmailTemplates((prev) => prev.filter((t) => !idsSet.has(t.id || "")));
    },
    []
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setEmailTemplate(null);
    setEmailTemplates([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsPreviewing(false);
    setIsPublishing(false);
    setRemovingEmailTemplateId(null);
    setSelectedEmailTemplate(null);
    setLoadingEmailTemplateIds([]);
  }, []);

  // Get single email template (matching API method name)
  const get = useCallback(
    async (id: string): Promise<EmailTemplate | null> => {
      addLoadingId(id);
      setLastError(null);

      try {
        const result = await EmailTemplateService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const emailTemplateData = result.data[0] as EmailTemplate;
          setEmailTemplate(emailTemplateData);
          updateOrAddEmailTemplateInList(emailTemplateData);
          setLastError(null);
          return emailTemplateData;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load email template";
          setLastError(errorMessage);
          setEmailTemplate(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load email template";
        setLastError(errorMessage);
        setEmailTemplate(null);
        return null;
      } finally {
        removeLoadingId(id);
      }
    },
    [updateOrAddEmailTemplateInList, addLoadingId, removeLoadingId]
  );

  // List email templates (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<EmailTemplateFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setEmailTemplates([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await EmailTemplateService.list(
          filter as EmailTemplateFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEmailTemplates = Array.isArray(result.data)
            ? result.data
            : [];

          if (isAppending) {
            setEmailTemplates((prev) => {
              const existingIds = new Set(prev.map((t) => t.id));
              const uniqueNewEmailTemplates = newEmailTemplates.filter(
                (t: any) => !existingIds.has(t.id)
              );
              return [...prev, ...uniqueNewEmailTemplates];
            });
          } else {
            setEmailTemplates(newEmailTemplates);
          }

          setHasMore(newEmailTemplates.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load email templates";
          setLastError(errorMessage);
          if (!isAppending) setEmailTemplates([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load email templates";
        setLastError(errorMessage);
        if (!isAppending) setEmailTemplates([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many email templates by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<EmailTemplate[]> => {
      if (ids.length === 0) {
        return [];
      }

      addLoadingIds(ids);
      setLastError(null);

      try {
        const result = await EmailTemplateService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedEmailTemplates = Array.isArray(result.data)
            ? result.data
            : [];
          loadedEmailTemplates.forEach((emailTemplate: any) => {
            updateOrAddEmailTemplateInList(emailTemplate);
          });
          setLastError(null);
          return loadedEmailTemplates;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load email templates";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load email templates";
        setLastError(errorMessage);
        return [];
      } finally {
        removeLoadingIds(ids);
      }
    },
    [updateOrAddEmailTemplateInList, addLoadingIds, removeLoadingIds]
  );

  // Create email template (matching API method name)
  const create = useCallback(
    async (params: EmailTemplateInput): Promise<EmailTemplate | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await EmailTemplateService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEmailTemplate = result.data[0] as EmailTemplate;
          setEmailTemplate(newEmailTemplate);
          setEmailTemplates((prev) => [newEmailTemplate, ...prev]);
          setLastError(null);
          return newEmailTemplate;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create email template";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create email template";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update email template (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: EmailTemplateInput
    ): Promise<EmailTemplate | null> => {
      if (!id) {
        setLastError("Email template ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await EmailTemplateService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedEmailTemplate = result.data[0] as EmailTemplate;
          setEmailTemplate(updatedEmailTemplate);
          updateOrAddEmailTemplateInList(updatedEmailTemplate);
          setLastError(null);
          return updatedEmailTemplate;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update email template";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update email template";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddEmailTemplateInList]
  );

  const publish = useCallback(
    async (id: string): Promise<EmailTemplate | null> => {
      if (!id) {
        setLastError("Email template ID is required for updating");
        return null;
      }

      setIsPublishing(true);
      setLastError(null);

      try {
        const result = await EmailTemplateService.publish({ id });

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedEmailTemplate = result.data[0] as EmailTemplate;
          setEmailTemplate(updatedEmailTemplate);
          updateOrAddEmailTemplateInList(updatedEmailTemplate);
          setLastError(null);
          return updatedEmailTemplate;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to publish email template";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to publish email template";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsPublishing(false);
      }
    },
    [updateOrAddEmailTemplateInList]
  );

  // Remove email template (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Email template ID is required for removal");
        return false;
      }

      setRemovingEmailTemplateId(id);
      setLastError(null);

      try {
        const result = await EmailTemplateService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmailTemplate(null);
          removeEmailTemplateFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove email template";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove email template";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingEmailTemplateId(null);
      }
    },
    [removeEmailTemplateFromList]
  );

  // Remove many email templates (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one email template ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await EmailTemplateService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmailTemplate(null);
          removeEmailTemplatesFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove email templates";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove email templates";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeEmailTemplatesFromList]
  );

  // Preview email template
  const preview = useCallback(
    async (id: string, sampleData?: string): Promise<EmailTemplate | null> => {
      if (!id) {
        setLastError("Email template ID is required for preview");
        return null;
      }

      setIsPreviewing(true);
      setLastError(null);

      try {
        const result = await EmailTemplateService.preview(id, sampleData);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const previewEmailTemplate = result.data[0] as EmailTemplate;
          setLastError(null);
          return previewEmailTemplate;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to preview email template";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to preview email template";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsPreviewing(false);
      }
    },
    []
  );

  // Load more email templates
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh email templates
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const prevUserIdRef = useRef(userId);

  useEffect(() => {
    // Reset when user changes
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      setHasAutoLoaded(false);
      reset();
    }

    if (autoLoad && userId && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, userId, hasAutoLoaded, list, pageSize, initialSort, reset]);

  return {
    emailTemplate,
    emailTemplates,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    loadingEmailTemplateIds,
    isLoadingEmailTemplate,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    isPreviewing,
    isPublishing,
    removingEmailTemplateId,
    selectedEmailTemplate,
    get,
    list,
    getMany,
    create,
    update,
    publish,
    remove,
    removeMany,
    preview,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedEmailTemplate,
    reset,
    lastError,
    clearError,
  };
};
